// app/src/lib/crash/redact.js — pure helpers for the anonymous crash reporter.
//
// Privacy rule: a crash report may only contain fields from a fixed schema, and
// the ONE freeform field (the error message) is scrubbed here before it leaves
// the device. Everything is pure/synchronous so it is unit-testable.
//
// The Lambda applies the SAME scrub server-side as defense-in-depth —
// infra/layers/shared/kanjo-shared/crashReport.mjs is a verbatim copy (the
// client bundle and the Lambda layer are separate trees, so the logic is
// duplicated on purpose). test/crashReport.test.js asserts the two copies
// stay behaviorally identical.

/**
 * Scrub an error message of anything that could be user data.
 * Kill order matters: URLs lose their query first (so tokens don't survive as
 * "digits"), then emails, then any quoted content (JSON.parse / Dexie errors
 * embed actual data inside quotes), then digit runs (amounts, phones, ZIPs).
 * @param {any} msg
 * @param {number} [max]
 * @returns {string}
 */
export function redactMessage(msg, max = 200) {
  let s = String(msg == null ? '' : msg);
  s = s.replace(/(https?:\/\/[^\s?#]+)[?#]\S*/g, '$1');
  s = s.replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '‹email›');
  s = s.replace(/"[^"]*"|'[^']*'|`[^`]*`/g, '‹str›');
  s = s.replace(/\d{3,}/g, '‹n›');
  return s.slice(0, max);
}

/**
 * Parse a stack trace into schema-safe frames: "file.js:line:col" with the
 * script's basename only (no origin, no path, no query). Minified prod frames
 * ("app.js:1:48213") carry no user data by construction; anything that doesn't
 * look like a script location is dropped.
 * Handles Chrome ("    at fn (https://host/app.js:1:2)") and Safari/Firefox
 * ("fn@https://host/app.js:1:2") formats.
 * @param {any} stack
 * @param {number} [max]
 * @returns {string[]}
 */
export function parseStack(stack, max = 10) {
  const frames = [];
  for (const line of String(stack || '').split('\n')) {
    if (frames.length >= max) break;
    const m = line.match(/([\w.$-]+\.(?:m?jsx?))(?:\?[^\s():]*)?:(\d+):(\d+)/);
    if (m) frames.push(`${m[1]}:${m[2]}:${m[3]}`);
  }
  return frames;
}

/**
 * FNV-1a 32-bit hash, hex-encoded. Synchronous on purpose (crypto.subtle is
 * async and unavailable in the Lambda's hot path anyway) — this is a grouping
 * key, not a security primitive.
 * @param {string} str
 * @returns {string} 8 hex chars
 */
export function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/**
 * Crash signature: same build + same error name + same top frame = same crash.
 * Build id is part of the key because minified line/col shift every deploy;
 * scripts/crash-symbolicate.mjs maps a frame back to source for cross-build
 * triage.
 * @param {{build: string, name: string, topFrame?: string}} p
 * @returns {string}
 */
export function fingerprint({ build, name, topFrame }) {
  return fnv1a(`${build}|${name}|${topFrame || ''}`);
}
